/**
 * usePopupChat Hook
 *
 * Manages creating, rendering, and cleaning up popup chat windows.
 * Uses ReactDOM.createRoot to render React components in the popup.
 */

import { useCallback, useRef, useState, useEffect, createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  registerPopup,
  unregisterPopup,
  focusExistingPopup,
  isPopupOpen,
} from '@/services/popupRegistry';
import type { Item } from '@/types';

// ============================================
// TYPES
// ============================================

interface UsePopupChatOptions {
  item: Item;
}

interface UsePopupChatReturn {
  /** Opens the popup window. Returns false if blocked by popup blocker. */
  openPopup: () => boolean;
  /** Whether a popup is currently open for this item */
  isPopupOpen: boolean;
}

// ============================================
// STYLE CLONING
// ============================================

/**
 * Clone all stylesheets from parent window to popup
 */
function cloneStylesToPopup(popup: Window): void {
  // Clone link stylesheets (production builds)
  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    const clone = link.cloneNode(true) as HTMLLinkElement;
    popup.document.head.appendChild(clone);
  });

  // Clone style tags (Vite dev mode injects styles this way)
  document.querySelectorAll('style').forEach((style) => {
    popup.document.head.appendChild(style.cloneNode(true));
  });

  // Add base styles for the popup
  const baseStyle = popup.document.createElement('style');
  baseStyle.textContent = `
    html, body {
      margin: 0;
      padding: 0;
      height: 100%;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    #popup-root {
      height: 100%;
    }
  `;
  popup.document.head.appendChild(baseStyle);
}

// ============================================
// HOOK
// ============================================

export function usePopupChat({ item }: UsePopupChatOptions): UsePopupChatReturn {
  const popupRef = useRef<Window | null>(null);
  const rootRef = useRef<Root | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // Check popup status periodically (window.closed doesn't trigger events)
  useEffect(() => {
    const interval = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        popupRef.current = null;
        rootRef.current = null;
        setPopupOpen(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const openPopup = useCallback(() => {
    // Check if already open - focus existing
    if (focusExistingPopup(item.id)) {
      setPopupOpen(true);
      return true;
    }

    // Calculate popup position (center of screen or near main window)
    const width = 420;
    const height = 650;
    const left = window.screenX + window.outerWidth - width - 50;
    const top = window.screenY + 100;

    // Open new popup window
    const popup = window.open(
      '',
      `chat-${item.id}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      // Popup blocked
      return false;
    }

    // Setup popup document
    popup.document.title = `Chat: ${item.title}`;

    // Clone styles
    cloneStylesToPopup(popup);

    // Set body classes for Tailwind
    popup.document.body.className = 'antialiased text-gray-900 bg-white';

    // Create container for React
    const container = popup.document.createElement('div');
    container.id = 'popup-root';
    popup.document.body.appendChild(container);

    // Dynamically import and render the PopupChatWindow component
    // We use dynamic import to avoid circular dependencies
    void import('@/components/ai/PopupChatWindow').then(({ PopupChatWindow }) => {
      const root = createRoot(container);
      root.render(
        createElement(PopupChatWindow, {
          itemId: item.id,
          itemTitle: item.title,
        })
      );
      rootRef.current = root;
    });

    // Cleanup function
    const cleanup = () => {
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
      unregisterPopup(item.id);
      setPopupOpen(false);
    };

    // Register cleanup handlers
    popup.addEventListener('beforeunload', cleanup);
    registerPopup(item.id, popup, cleanup);

    popupRef.current = popup;
    setPopupOpen(true);

    return true;
  }, [item.id, item.title]);

  return {
    openPopup,
    isPopupOpen: popupOpen || isPopupOpen(item.id),
  };
}
